/**
 * What a trip's Faye travel insurance is, and what it means for the trip's
 * money. Plain JS on purpose, like stay-cancel-rules.js: the Trips page, the
 * Express server and the edge functions all read a policy the same way.
 *
 * ## The money model
 *
 * The premium is part of what the customer pays, like the hotel on a
 * package: it sits inside the booking's `total_amount`, `grand_total` and
 * `grand_total_with_hold`, so every schedule, payoff and receipt carries it
 * on its own. It is recorded separately in `bookings.insurance_premium_cents`
 * (frozen against customer edits, migration 156), and everything that
 * measures Defera's fees, margin, miles or the base fare takes it back out.
 * Defera's own income from insurance is Faye's commission, never the premium.
 *
 * The policy itself — plan, quote, accepted disclosures, status, policy
 * number — lives in `trip_insurance_policies` (service role only). The
 * booking endpoints attach it to each booking as `trip_insurance`, with the
 * columns a customer may see.
 *
 * ## Never confirm when the policy is bought
 *
 * Defera buys the policy through Faye by hand once the trip is ticketed.
 * Whether a trip is booked with the airline is never confirmed or denied to
 * customers (concierge Hard Rule #15), and Defera never tells a customer when
 * their policy is bought (founder decision, 2026-10-02). So a customer sees
 * an issued policy exactly like one not yet bought until the trip is paid in
 * full, when its number is released alongside the airline confirmation
 * (customerPolicyView). Nothing customer-facing ties the policy to ticketing.
 */

export const INSURANCE_STATUS = Object.freeze({
  /** Sold at checkout; Defera issues it through Faye before the trip. */
  PENDING: 'pending',
  /** Bought in Faye's portal; `policy_number` is set. */
  ISSUED: 'issued',
  /** The trip ended before the policy was issued: the premium collected goes back. */
  REFUND_DUE: 'refund_due',
  REFUNDED: 'refunded',
  /** The trip ended after the policy was issued: ops cancels it with Faye
   *  (inside Faye's 14-day free look) or decides to keep it. */
  NEEDS_CANCELLATION: 'needs_cancellation',
  CANCELLED: 'cancelled',
});

const STATUSES = new Set(Object.values(INSURANCE_STATUS));

/** Allowed ops moves. Anything else is a mistake the endpoint refuses. */
export const INSURANCE_TRANSITIONS = Object.freeze({
  pending: ['issued', 'refund_due', 'cancelled'],
  issued: ['needs_cancellation', 'cancelled'],
  refund_due: ['refunded'],
  refunded: [],
  // Faye refunds a free-look cancellation to Defera, which then owes the customer.
  needs_cancellation: ['cancelled', 'refund_due', 'issued'],
  cancelled: [],
});

const toCents = (raw) => {
  if (raw == null || raw === '') return 0;
  const n = Math.round(Number(raw));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Cents of Faye premium inside one booking row's totals; 0 when uninsured. */
export function insurancePremiumCents(booking) {
  if (!booking || typeof booking !== 'object') return 0;
  return toCents(booking.insurance_premium_cents ?? booking.insurancePremiumCents);
}

/** The premium across every row (leg) of a trip, in cents. */
export function tripInsurancePremiumCents(rows) {
  return (Array.isArray(rows) ? rows : [rows]).reduce((sum, row) => sum + insurancePremiumCents(row), 0);
}

const MONEY_COLUMNS = [
  'total_amount', 'totalPrice', 'total_price', 'totalAmount',
  'grand_total', 'grandTotal', 'grand_total_with_hold', 'grandTotalWithHold',
];

/**
 * The same booking row with the premium taken out of its total columns: what
 * the row looked like before insurance existed. Fee, base-fare and margin
 * math runs on this, so none of it ever counts the premium. Returns the row
 * itself when it carries no premium. The copy carries no premium of its own,
 * so stripping it twice is harmless.
 */
export function withoutInsurancePremium(booking) {
  const cents = insurancePremiumCents(booking);
  if (!cents) return booking;
  const out = { ...booking, insurance_premium_cents: null, insurancePremiumCents: null };
  for (const key of MONEY_COLUMNS) {
    const raw = booking[key];
    if (raw == null || raw === '') continue;
    const value = Number(raw);
    // Only a total that can contain the premium has it taken out.
    if (Number.isFinite(value) && value * 100 >= cents) out[key] = Math.round(value * 100 - cents) / 100;
  }
  return out;
}

const PLAN_NAMES = { first: 'Faye First', forward: 'Faye Forward', flagship: 'Faye Flagship' };
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * The policy the booking endpoints attached (`trip_insurance`, an object or
 * a one-row array), normalized; null when the booking has none.
 */
export function readTripInsurance(booking) {
  const raw = booking?.trip_insurance ?? booking?.tripInsurance;
  const row = Array.isArray(raw) ? raw.find((r) => r && typeof r === 'object') : raw;
  if (!row || typeof row !== 'object') return null;
  const premiumCents = toCents(row.premium_cents);
  if (!premiumCents) return null;
  const plan = str(row.plan) || 'first';
  const coverage = row.coverage && typeof row.coverage === 'object' ? row.coverage : {};
  return {
    id: row.id ?? null,
    bookingId: row.booking_id ?? booking?.id ?? null,
    plan,
    label: str(row.plan_label) || PLAN_NAMES[plan] || 'Faye',
    international: row.international === true,
    premiumCents,
    insuredCostCents: toCents(row.insured_cost_cents),
    status: STATUSES.has(row.status) ? row.status : INSURANCE_STATUS.PENDING,
    policyNumber: str(row.policy_number),
    refundCents: toCents(row.refund_cents),
    /** False while a refund's amount is still ops' call (a default). */
    refundKnown: row.refund_cents != null,
    refundMethod: str(row.refund_method),
    refundedAt: row.refunded_at ?? null,
    highlights: Array.isArray(coverage.highlights) ? coverage.highlights.filter((h) => typeof h === 'string') : [],
    benefits: coverage.coverage && typeof coverage.coverage === 'object' ? coverage.coverage : {},
    excludedReasons: Array.isArray(coverage.excludedReasons) ? coverage.excludedReasons : [],
    travelers: Array.isArray(row.travelers) ? row.travelers : [],
    departDate: str(row.depart_date),
    returnDate: str(row.return_date),
    residenceState: str(row.residence_state),
    acceptedAt: row.disclosures_accepted_at ?? null,
    // Ops-only; the customer endpoint never sends these.
    issuedAt: row.issued_at ?? null,
    fayePremiumCents: toCents(row.faye_premium_cents),
    quote: row.quote && typeof row.quote === 'object' ? row.quote : null,
    opsNote: str(row.ops_note),
    // Re-prices after a flight change (migration 159, _shared/faye-reprice.js).
    adjustments: Array.isArray(row.adjustments) ? row.adjustments.filter((a) => a && typeof a === 'object') : [],
  };
}

/** Columns a customer may receive. No ops notes, no Faye cost, no issue or
 *  ticketing dates (see the header). */
export const CUSTOMER_POLICY_COLUMNS = Object.freeze([
  'id', 'booking_id', 'provider', 'plan', 'plan_label', 'international', 'premium_cents', 'insured_cost_cents',
  'coverage', 'travelers', 'depart_date', 'return_date', 'residence_state', 'disclosure_version',
  'disclosures_accepted_at', 'status', 'policy_number', 'refund_cents', 'refund_method', 'refunded_at',
]);

/** Trip statuses at which the airline confirmation is released to the
 *  customer (paid in full). Same set the concierge desk reads
 *  (server/tommy-booking-context.js COMPLETED_STATUSES). */
export const POLICY_RELEASE_STATUSES = Object.freeze(['completed', 'ticketed', 'paid_in_full', 'paid', 'fulfilled']);

/** Whether the trip's documents are released to the customer yet. */
export function isTripReleased(booking) {
  const status = String(booking?.status ?? booking?.trip_status ?? '').toLowerCase();
  return POLICY_RELEASE_STATUSES.includes(status);
}

/**
 * The policy row as a customer may see it. Until the trip is released (paid
 * in full), an issued policy reads as pending and its number is held back,
 * like the airline confirmation: Defera never confirms when it bought the
 * policy. A policy number only ever shows on an issued policy.
 */
export function customerPolicyView(row, booking) {
  if (!row || typeof row !== 'object') return row;
  const issued = row.status === INSURANCE_STATUS.ISSUED || row.status === INSURANCE_STATUS.NEEDS_CANCELLATION;
  if (issued && isTripReleased(booking)) return row;
  return { ...row, status: issued ? INSURANCE_STATUS.PENDING : row.status, policy_number: null };
}

/** Readable benefit names for the coverage keys stored with each policy. */
export const BENEFIT_LABELS = Object.freeze({
  cancellation: 'Trip cancellation',
  interruption: 'Trip interruption',
  delay: 'Flight delay',
  missedConnection: 'Missed connection',
  inconvenience: 'Travel inconvenience',
  medical: 'Emergency medical',
  evacuation: 'Medical evacuation',
  bedsideCompanion: 'Bedside companion',
  baggage: 'Lost or damaged bags',
  baggageDelay: 'Delayed bags',
});

const dollars = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * What the customer's policy card says about where things stand, from a
 * policy already passed through customerPolicyView. Tones:
 * 'pending' | 'active' | 'refund' | 'closed'. Refunds give no reason: the
 * reason would describe when the policy was (not) bought.
 *
 * @param {object} policy  readTripInsurance() of a customer view
 * @param {{ tripActive?: boolean }} [opts]  false once the trip is cancelled or defaulted
 */
export function insuranceCardState(policy, { tripActive = true } = {}) {
  if (!policy) return null;
  // Premium refunds go back to the card, never as Defera Credit (PREMIUM_REFUND_METHOD).
  const refundTo = 'to your card';
  // Neutral on purpose: "this policy" or "with Faye" would say one was bought.
  const settling = {
    tone: 'pending',
    label: 'Being settled',
    detail: 'Our team is settling the travel insurance part of this trip.',
  };
  switch (policy.status) {
    case INSURANCE_STATUS.ISSUED:
    case INSURANCE_STATUS.NEEDS_CANCELLATION:
      return {
        tone: 'active',
        label: 'Active',
        detail: 'Your policy is in place. Manage it and file claims in the Faye app.',
      };
    case INSURANCE_STATUS.REFUND_DUE:
      // A default leaves the amount to ops: say nothing about a refund yet.
      if (policy.refundKnown === false) return settling;
      return {
        tone: 'refund',
        label: 'Refund on the way',
        detail: `The travel insurance part of your payments is being refunded ${refundTo}.`,
      };
    case INSURANCE_STATUS.REFUNDED:
      return {
        tone: 'refund',
        label: 'Refunded',
        detail: policy.refundCents > 0
          ? `${dollars(policy.refundCents)} of travel insurance was refunded ${refundTo}.`
          : 'No travel insurance was charged on this trip.',
      };
    case INSURANCE_STATUS.CANCELLED:
      return { tone: 'closed', label: 'Cancelled', detail: 'The travel insurance on this trip was cancelled.' };
    default:
      if (!tripActive) return settling;
      return {
        tone: 'pending',
        label: 'Included',
        detail: 'Faye issues your policy before you travel. Your policy number shows here once your trip is paid in full.',
      };
  }
}

/**
 * Premium the customer has paid so far, in cents. The premium rides on the
 * payments after the deposit, so it is collected in proportion to them:
 * every installment carries the same share of the premium.
 *
 * @param {object} p
 * @param {number} p.premiumCents         the policy's premium
 * @param {number} p.planTotalCents       the booking's total, premium included
 * @param {number} p.depositCents         the deposit (never carries premium)
 * @param {number} p.paidCents            everything paid toward the plan, deposit included
 */
export function premiumCollectedCents({ premiumCents, planTotalCents, depositCents, paidCents }) {
  const premium = toCents(premiumCents);
  const financed = toCents(planTotalCents) - toCents(depositCents);
  if (!premium || financed <= 0) return 0;
  const paidAfterDeposit = Math.min(financed, Math.max(0, toCents(paidCents) - toCents(depositCents)));
  return Math.min(premium, Math.round((premium * paidAfterDeposit) / financed));
}

/**
 * Where premium collected for a policy that was never issued goes when the
 * trip ends: back to the customer's card, never into Defera Credit (the
 * premium is collected for Faye; keeping it as house credit would read as
 * keeping it). Founder decision D2 in the launch audit. The cancel screen and
 * the cancel endpoints both read this, so they can't disagree.
 */
export const PREMIUM_REFUND_METHOD = 'card';

/**
 * The marker a trip carries when insurance was added after booking
 * (faye-post-booking.js, migration 161): `{ at, mode: 'spread'|'upfront',
 * tier, processor, premium_cents, payments, plan_paid_before_cents }`, or
 * null. Spread: the premium joined the plan for the payments left. Upfront:
 * it was charged once, outside the plan, so the totals and
 * `insurance_premium_cents` never carry it.
 */
export function insuranceAddedAfterBooking(booking) {
  const fd = booking?.flight_data ?? booking?.flightData;
  const m = fd && typeof fd === 'object' ? fd._trip_insurance_added : null;
  if (!m || typeof m !== 'object' || (m.mode !== 'spread' && m.mode !== 'upfront')) return null;
  return m;
}

/** Whether a booking has (or may have) a policy row to look up. */
export function mayHaveTripInsurance(booking) {
  return insurancePremiumCents(booking) > 0 || insuranceAddedAfterBooking(booking) !== null;
}

/** Whether any row (leg) of a trip carries travel insurance, however it was paid. */
export function tripHasInsurance(rows) {
  return (Array.isArray(rows) ? rows : [rows]).some((row) => Boolean(row) && mayHaveTripInsurance(row));
}

/** Every cent of Faye premium on a trip: inside the plan, plus anything
 *  charged upfront (upfrontAddPremiumCents) when it was added after booking. */
export function tripInsuranceTotalCents(rows) {
  const list = Array.isArray(rows) ? rows : [rows];
  return tripInsurancePremiumCents(list) + list.reduce((sum, row) => sum + upfrontAddPremiumCents(row), 0);
}

/**
 * The cancel screen's travel insurance split, mirroring the settlement
 * (trip-insurance-settlement.js) closely enough to show before the cancel;
 * the cancel endpoint recomputes it exactly. Only a policy not yet issued is
 * refunded.
 *
 * - `refundCents`: what goes back to the card.
 * - `insidePaidCents`: how much of that sits inside `paidCents` (the cash the
 *   cancel turns into Defera Credit), so it comes off the credit.
 *
 * @param {object} p
 * @param {ReturnType<typeof readTripInsurance>} p.policy
 * @param {object} p.booking          the trip's main booking row (totals, flight_data)
 * @param {number} p.paidCents        paid toward the plan, deposit included
 */
export function cancelInsuranceSplit({ policy, booking, paidCents }) {
  const none = { refundCents: 0, insidePaidCents: 0 };
  if (!policy || policy.status !== INSURANCE_STATUS.PENDING) return none;
  const paid = Math.max(0, toCents(paidCents));
  const added = insuranceAddedAfterBooking(booking);
  // Charged once, outside the plan: all of it back to the card, none of it in `paid`.
  if (added?.mode === 'upfront') return { refundCents: toCents(added.premium_cents) || policy.premiumCents, insidePaidCents: 0 };
  // What the payments carry is checkout's (the booking's); a flight change
  // re-prices the policy and settles that on its own.
  const premium = insurancePremiumCents(booking) || policy.premiumCents;
  const planTotal = toCents(Math.round((Number(booking?.grand_total_with_hold ?? booking?.grandTotalWithHold ?? booking?.grand_total ?? booking?.grandTotal) || 0) * 100));
  let share;
  if (added?.mode === 'spread' && added.plan_paid_before_cents != null) {
    // Spread over what was left when it was added: only later payments carry it.
    share = premiumShareAfterAddCents({ premiumCents: premium, planTotalCents: planTotal, planPaidBeforeCents: added.plan_paid_before_cents, paidCents: paid });
  } else {
    const fd = booking?.flight_data ?? booking?.flightData ?? {};
    share = premiumCollectedCents({
      premiumCents: premium,
      planTotalCents: planTotal,
      depositCents: Number(fd?.payment_schedule?.deposit_amount_cents) || 0,
      paidCents: paid,
    });
  }
  const inside = Math.min(share, paid);
  return { refundCents: inside, insidePaidCents: inside };
}

/**
 * The premium's share of `paidCents` on a plan the premium was spread into
 * AFTER booking: it rides only on what was paid past `planPaidBeforeCents`
 * (the plan paid when it was added, deposit included), over what was left
 * then plus the premium (`planTotalCents − planPaidBeforeCents`). Checkout's
 * even spread after the deposit (premiumCollectedCents) would put premium in
 * payments made before the add and overstate it.
 */
export function premiumShareAfterAddCents({ premiumCents, planTotalCents, planPaidBeforeCents, paidCents }) {
  const premium = toCents(premiumCents);
  const before = toCents(planPaidBeforeCents);
  const financed = toCents(planTotalCents) - before;
  if (!premium || financed <= 0) return 0;
  return Math.min(premium, Math.round((premium * Math.min(Math.max(0, toCents(paidCents) - before), financed)) / financed));
}

/** A premium charged once, outside the plan, when insurance was added to a
 *  paid-off trip: never in the booking's totals or its premium column. */
export function upfrontAddPremiumCents(booking) {
  const added = insuranceAddedAfterBooking(booking);
  return added?.mode === 'upfront' ? toCents(added.premium_cents) : 0;
}

/** About how much each remaining payment carries, rounded up to the cent. */
export function premiumPerPaymentCents(premiumCents, payments) {
  const n = Math.floor(Number(payments));
  const premium = toCents(premiumCents);
  if (!premium || !(n >= 1)) return 0;
  return Math.ceil(premium / n);
}

/** States whose residents get their own plan document; everyone else gets
 *  Faye's countrywide (CW) one. */
const FAYE_STATE_DOCUMENTS = new Set(['NY', 'WA', 'MT', 'MO', 'NH', 'IL', 'IN', 'OR']);
const DOCUMENT_PLAN_NAMES = { first: 'First', forward: 'Forward', flagship: 'Flagship' };

/**
 * Faye's plan document for a resident of `state`, or null when there isn't a
 * known one: Faye First International's document hasn't been confirmed, so
 * those customers get theirs from Faye's email instead of a guess.
 */
export function fayePlanDocumentUrl(plan, state, { international = false } = {}) {
  const name = DOCUMENT_PLAN_NAMES[plan];
  if (!name || (plan === 'first' && international)) return null;
  const st = String(state || '').trim().toUpperCase();
  const region = FAYE_STATE_DOCUMENTS.has(st) ? st : 'CW';
  return `https://assets.withfaye.com/downloads/${name}%20-%20Standard%20${region}.pdf`;
}

/** Faye's customer support, for coverage questions Defera may not answer. */
export const FAYE_SUPPORT = Object.freeze({
  email: 'support@withfaye.com',
  phoneUs: '+1-833-240-7056',
  phoneIntl: '+1-804-482-2122',
});
